import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  Phone, ChevronRight, CheckCircle, Clock, Shield, 
  ArrowLeft, DoorOpen, Key, Lock, Wrench 
} from "lucide-react";
import HeroBanner from "../components/HeroBanner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [config, setConfig] = useState(null);
  const [otherServices, setOtherServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/services/${slug}`).then(res => res.json()),
      fetch(`${API_URL}/api/config`).then(res => res.json()),
      fetch(`${API_URL}/api/services`).then(res => res.json())
    ])
      .then(([serviceData, configData, servicesData]) => {
        setService(serviceData);
        setConfig(configData);
        setOtherServices(servicesData.filter(s => s.slug !== slug).slice(0, 4));
        setLoading(false);
      })
      .catch(console.error);
  }, [slug]);

  const getServiceIcon = (iconName) => {
    const icons = {
      DoorOpen: DoorOpen,
      Lock: Lock,
      Key: Key,
      KeyRound: Key,
      Wrench: Wrench,
      Cylinder: Lock,
      ShieldAlert: Shield
    };
    const IconComponent = icons[iconName] || Lock;
    return <IconComponent className="w-10 h-10" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0f2744]"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0f2744] mb-4">Service non trouvé</h1>
          <Link to="/services" className="btn-primary">
            Voir tous les services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="service-detail-page">
      {/* Hero */}
      <HeroBanner
        title={service.name}
        subtitle={service.description}
        highlightText="Fichet"
        showFeatures={false}
        showCTA={true}
        config={config}
        variant="compact"
        breadcrumb={
          <Link 
            to="/services" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Tous les services
          </Link>
        }
      />

      {/* Trust bar */}
      <section className="bg-white py-4 border-b border-gray-100 sticky top-16 md:top-20 z-40 shadow-sm">
        <div className="container-custom">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <div className="flex flex-wrap gap-6 text-sm">
              <span className="flex items-center gap-2 text-gray-600">
                <Clock className="w-4 h-4 text-amber-500" />
                Intervention rapide
              </span>
              <span className="flex items-center gap-2 text-gray-600">
                <Shield className="w-4 h-4 text-green-500" />
                Devis gratuit
              </span>
            </div>
            {config && (
              <a
                href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
                className="btn-cta text-sm py-2"
                data-testid="service-detail-cta"
              >
                <Phone className="w-4 h-4 mr-2" />
                {config.phone}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main content */}
            <div className="lg:col-span-2">
              {service.image_url && (
                <img
                  src={service.image_url}
                  alt={service.name}
                  className="w-full h-64 md:h-80 object-cover rounded-2xl mb-8"
                />
              )}

              <div className="prose prose-lg max-w-none">
                {service.long_description.split('\n').map((paragraph, index) => {
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return (
                      <h3 key={index} className="text-xl font-bold text-[#0f2744] mt-8 mb-4">
                        {paragraph.replace(/\*\*/g, '')}
                      </h3>
                    );
                  }
                  if (paragraph.startsWith('- ')) {
                    return (
                      <div key={index} className="flex items-start gap-3 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                        <span className="text-gray-600">{paragraph.replace('- ', '')}</span>
                      </div>
                    );
                  }
                  if (paragraph.trim()) {
                    return (
                      <p key={index} className="text-gray-600 mb-4">
                        {paragraph}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>

              {/* Keywords for SEO */}
              {service.keywords && service.keywords.length > 0 && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-500 mb-3">Recherches associées</h4>
                  <div className="flex flex-wrap gap-2">
                    {service.keywords.map((keyword, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* CTA Card */}
              <div className="bg-gradient-to-br from-[#0f2744] to-[#1a3a5c] text-white rounded-2xl p-6 mb-6 sticky top-40">
                <h3 className="text-xl font-bold mb-4">
                  Besoin de ce service ?
                </h3>
                <p className="text-gray-300 text-sm mb-6">
                  Nos techniciens interviennent rapidement pour tous vos problèmes de serrurerie Fichet.
                </p>
                
                <div className="space-y-4">
                  {config && (
                    <a
                      href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
                      className="btn-cta w-full flex items-center justify-center gap-2"
                      data-testid="sidebar-phone-cta"
                    >
                      <Phone className="w-5 h-5" />
                      {config.phone}
                    </a>
                  )}
                  <Link
                    to="/contact"
                    className="btn-outline w-full border-white text-white hover:bg-white hover:text-[#0f2744] flex items-center justify-center"
                  >
                    Demander un devis
                  </Link>
                </div>

                <div className="mt-6 pt-6 border-t border-white/20 space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span>Intervention en 30 min</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-400" />
                    <span>Devis gratuit</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-400" />
                    <span>7j/7 - 24h/24</span>
                  </div>
                </div>
              </div>

              {/* Other services */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="font-bold text-[#0f2744] mb-4">
                  Autres services
                </h3>
                <div className="space-y-3">
                  {otherServices.map((s) => (
                    <Link
                      key={s.id}
                      to={`/services/${s.slug}`}
                      className="flex items-center justify-between py-2 text-gray-600 hover:text-amber-600 transition-colors group"
                    >
                      <span>{s.name}</span>
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schema.org structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": service.name,
          "description": service.description,
          "provider": {
            "@type": "LocalBusiness",
            "name": "Fichet Dépannage",
            "telephone": config?.phone || "09 72 57 44 50"
          },
          "areaServed": "Île-de-France",
          "serviceType": "Serrurerie"
        })
      }} />
    </div>
  );
}
